import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Asset, Task, ViewMode as GanttViewMode } from "@/controls/ganttAsset";
import { GanttData, GanttEvent, ViewMode } from "../models/types";
import { getGanttData } from "../repositories/ganttRepository";
import {
	createProcessLine,
	createReclassifiedTask,
	createWorkOrderTask,
	withDelayLines,
} from "../taskBuilders";
import {
	CUTBACK_COLOR,
	CUTBACK_COLOR_LIGHT,
	CUTBACK_COLOR_LIGHTER,
	GanttLineColors,
	WELD165_COLORS,
	WELD165_NAME,
} from "@/shared/colors";
import { WeldLine, useWeldLines } from "../../../hooks/useWeldLines";
import { useSawLines } from "../../../hooks/useSawLines";

const DEFAULT_WELD_LINE_COUNT = 5;

const getTickCount = (
	startDate: Date,
	endDate: Date,
	ganttViewMode: GanttViewMode,
): number => {
	const diffMs = endDate.getTime() - startDate.getTime();
	const hours = diffMs / (1000 * 60 * 60);

	switch (ganttViewMode) {
		case GanttViewMode.Hour:
			return Math.ceil(hours);
		case GanttViewMode.TwoHour:
			return Math.ceil(hours / 2);
		case GanttViewMode.QuarterDay:
			return Math.ceil(hours / 6);
		case GanttViewMode.HalfDay:
			return Math.ceil(hours / 12);
		case GanttViewMode.Day:
			return Math.ceil(hours / 24);
		case GanttViewMode.Week:
			return Math.ceil(hours / 168);
		case GanttViewMode.Month:
			return Math.ceil(hours / 720);
		default:
			return Math.ceil(hours);
	}
};

const getEventLabel = (event: GanttEvent): string => {
	// Run/WO positions live in the tooltip; the bar keeps the shift number.
	return event.ShiftBundleNo == null ? "" : `${event.ShiftBundleNo}`;
};

const createTask = (event: GanttEvent, colors: GanttLineColors): Task => {
	const isBatch = event.EventType === "Batch";
	const label = isBatch ? "Batch" : "Bundle";
	const id = isBatch ? event.BatchId : event.BundleId;
	const isCutback = event.IsCutback === true;

	return {
		id: `${event.EventType.toLowerCase()}-${id}`,
		start: event.Start,
		end: event.End,
		processStart: event.Start,
		type: "task",
		progress: 100,
		name: `${label} ${id}`,
		displayName: isCutback ? "Cutback" : getEventLabel(event),
		processLines: [],
		source: event,
		styles: isCutback
			? {
					backgroundColor: CUTBACK_COLOR_LIGHT,
					backgroundSelectedColor: CUTBACK_COLOR_LIGHTER,
					progressColor: CUTBACK_COLOR,
					borderColor: CUTBACK_COLOR,
				}
			: {
					backgroundColor: colors.light,
					backgroundSelectedColor: colors.lighter,
					progressColor: colors.main,
					borderColor: colors.main,
				},
		textLeftTop: "",
		textRightTop: "",
		textLeftBottom: "",
		textRightBottom: "",
	};
};

const transformToAssetsMulti = (
	lines: WeldLine[],
	data: GanttData | undefined,
	startDate: Date,
	endDate: Date,
): Asset[] => {
	if (!data || lines.length === 0) return [];

	const { events, delays, workOrders } = data;

	return lines.map((line) => {
		const colors = line.colors;

		const lineEvents = events.filter((e) => e.AssetId === line.AssetId);
		const lineDelays = delays.filter((d) => d.AssetId === line.AssetId);
		const lineWorkOrders = workOrders.filter((w) => w.AssetId === line.AssetId);
		const lineReclassified = lineEvents.filter(
			(e) => e.IsReclassified === true,
		);

		const processLines = lineDelays.map((delay) => createProcessLine(delay));
		const tasks = withDelayLines(
			lineEvents.map((event) => createTask(event, colors)),
			processLines,
			startDate,
			endDate,
		);

		return {
			id: line.AssetName,
			name: line.AssetName,
			processes: [
				...tasks,
				...lineWorkOrders.map(createWorkOrderTask),
				...lineReclassified.map(createReclassifiedTask),
			],
			color: colors.light,
			colorGradientEnd: colors.lighter,
			hasBottomLane: lineWorkOrders.length > 0 || lineReclassified.length > 0,
		};
	});
};

const transformToAssetsSingle = (
	data: GanttData | undefined,
	startDate: Date,
	endDate: Date,
): Asset[] => {
	if (!data) return [];

	const { events, delays, workOrders } = data;
	const processLines = delays.map((delay) => createProcessLine(delay));
	const tasks = withDelayLines(
		events.map((event) => createTask(event, WELD165_COLORS)),
		processLines,
		startDate,
		endDate,
	);
	const name = events[0]?.AssetName ?? WELD165_NAME;

	return [
		{
			id: name,
			name,
			processes: [
				...tasks,
				...workOrders.map(createWorkOrderTask),
				...events
					.filter((e) => e.IsReclassified === true)
					.map(createReclassifiedTask),
			],
			color: WELD165_COLORS.light,
			colorGradientEnd: WELD165_COLORS.lighter,
			hasBottomLane: true,
		},
	];
};

interface UseGanttDataProps {
	startDate: Date;
	endDate: Date;
	viewMode: ViewMode;
	ganttViewMode: GanttViewMode;
	containerWidth: number;
	onError?: (error: Error) => void;
	/** Home: one lane per weld line. Overview (default): a single line only. */
	multiLine?: boolean;
	/** Single-line mode target (null = Weld165 back-compat). Ignored when multiLine. */
	assetId?: number | null;
}

export const useGanttData = ({
	startDate,
	endDate,
	viewMode,
	ganttViewMode,
	containerWidth,
	onError,
	multiLine = false,
	assetId = null,
}: UseGanttDataProps) => {
	const { weldLines, isLoading: weldLinesLoading } = useWeldLines({
		enabled: multiLine,
		onError,
	});
	const { sawLines, isLoading: sawLinesLoading } = useSawLines({
		enabled: multiLine,
		onError,
	});

	// Weld lanes first, then the tracked saw(s) below them.
	const lines = useMemo<WeldLine[]>(
		() => [...weldLines, ...sawLines],
		[weldLines, sawLines],
	);
	const assetIds = useMemo(() => lines.map((l) => l.AssetId), [lines]);

	const queryEnabled = multiLine ? assetIds.length > 0 : true;

	const {
		data,
		isLoading: ganttLoading,
		refetch,
	} = useQuery<GanttData, Error>({
		queryKey: [
			"ganttData",
			startDate.toISOString(),
			endDate.toISOString(),
			viewMode,
			multiLine
				? `multi:${assetIds.join(",")}`
				: `single:${assetId ?? "default"}`,
		],
		queryFn: () =>
			multiLine
				? getGanttData(startDate, endDate, viewMode, null, assetIds)
				: getGanttData(startDate, endDate, viewMode, assetId, null),
		enabled: queryEnabled,
		refetchInterval: 60000,
		meta: { realtime: ["FAB.GetGanttData"] },
		onError,
	});

	const isLoading =
		(multiLine && (weldLinesLoading || sawLinesLoading)) ||
		(queryEnabled && ganttLoading);

	const assets = useMemo(
		() =>
			multiLine
				? transformToAssetsMulti(lines, data, startDate, endDate)
				: transformToAssetsSingle(data, startDate, endDate),
		[multiLine, lines, data, startDate, endDate],
	);

	// Calculate columnWidth based on tick count to fill available space
	const columnWidth = useMemo(() => {
		if (containerWidth === 0) return 60;
		const tickCount = getTickCount(startDate, endDate, ganttViewMode);
		const assetListWidth = 160; // default assetCellWidth
		const availableWidth = containerWidth - assetListWidth - 40; // 40px padding
		const calculatedWidth = Math.floor(availableWidth / Math.max(tickCount, 1));
		return Math.max(calculatedWidth, 60); // minimum 60px per column
	}, [containerWidth, startDate, endDate, ganttViewMode]);

	const laneCount = multiLine ? lines.length || DEFAULT_WELD_LINE_COUNT : 1;

	return {
		// Layout
		columnWidth,
		laneCount,
		// Data
		assets,
		isLoading,
		refetch,
	};
};
